import {
  Component,
  Inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  CellClickedEvent,
  ColDef,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  ITooltipParams,
  PaginationChangedEvent,
  SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { SharedModule } from '../../shared/shared.module';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import {
  IngredientDetails,
  IngredientNutritionalInfo,
  RowData,
} from '../../../shared/model/ingredient-interface';
import { LibraryService } from '../../../services/library.service';
import { TableGridComponent } from '../../../shared/components/table-grid';
import { CONSTANTS_DETAILS, TABLE_CONSTANTS } from '../../../shared/constant';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../services/settings-service';
import {
  CountryResponseType,
  ManageLookUpTableData,
  RecipeRegion,
} from '../../../shared/model';
import { debounceTime, forkJoin, Subject } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import {
  convertToDecimel,
  gridCellDefaultView,
  gridCellValueEdit,
  gridDateFormatter,
  gridRefreshCells,
} from '../../../shared';
import { AuthService } from '../../../services/auth';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatDivider } from '@angular/material/divider';
import { DatePipe } from '@angular/common';
import { UpdateAspAlertDialogComponent } from '../update-asp-alert-dialog/update-asp-alert-dialog.component';

@Component({
  selector: 'app-update-nutritional-information-dialog',
  imports: [
    SharedModule,
    MatDialogModule,
    TableGridComponent,
    MatTabGroup,
    MatTab,
    MatDivider,
  ],
  templateUrl: './update-nutritional-information-dialog.component.html',
  styleUrl: './update-nutritional-information-dialog.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateNutritionalInformationDialogComponent
  implements OnInit, OnDestroy
{
  public gridRowData = signal<IngredientNutritionalInfo[]>([]);
  public rowModelSwitch$ = new Subject<boolean>();
  public gridOptions = signal<GridOptions>({});
  public columnDefs = signal<ColDef[]>(
    TABLE_CONSTANTS.INGREDIENT_NUTRITIONAL_INFO_TABLE,
  );
  public activeNewRow = false;
  public regionMaster: ManageLookUpTableData[] = [];
  public regions: string[] = [];
  public countryMaster: CountryResponseType[] = [];
  public availableNutriCountries: string[] = [];
  public isActiveEdit = signal<boolean>(false);
  private _authService = inject(AuthService);
  public selectedIndex: number = 0;
  public isSearch: boolean = true;

  /**
   * Creates an instance of UpdateNutritionalInformationDialogComponent.
   * @param {*} _data
   * @param {LibraryService} _libraryService
   * @param {TranslateService} _translate
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public _data: IngredientDetails,
    private _libraryService: LibraryService,
    private _settingsService: SettingsService,
    private _translate: TranslateService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    private _dialog: MatDialog,
  ) {}

  /**
   * Initializes the component and fetches the ingredient nutritional info.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  ngOnInit() {
    this.gridOptions.set(this.createServerSideGridOptions());
    this.gridSwitchListener();
    this.onTabChange(this.selectedIndex);
  }

  /**
   * Fetches the ingredient nutritional data from the server and initializes the grid.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private getIngredientNutritionalInfo() {
    if (this._data.ingredientTypeIdentifier !== 'INT-5') {
      forkJoin([
        this._settingsService.fetchLookupData(
          CONSTANTS_DETAILS.LOOKUP_TYPES[5].BASE_URL,
        ),
        this._settingsService.countryLookupData(),
        this._libraryService.getIngredientNutritionalInfo(
          this._data?.ingredientId,
        ),
      ]).subscribe(([regionMaster, countryMaster, ingredientMaster]) => {
        if ('regions' in regionMaster && Array.isArray(regionMaster.regions)) {
          this.regionMaster = (
            (regionMaster.regions as ManageLookUpTableData[]) || []
          ).filter((region: ManageLookUpTableData) => region.isEnabled);
          this.regions = this.regionMaster.map(
            (region: ManageLookUpTableData) => region.name,
          );
        }
        if (countryMaster && 'countryDetails' in countryMaster) {
          this.countryMaster = (
            countryMaster as { countryDetails: CountryResponseType[] }
          ).countryDetails.filter(
            (country: CountryResponseType) =>
              country.isEnabled && this.regions.includes(country.regionName),
          );
        }
        if (ingredientMaster?.ingredientNutritions) {
          this.gridRowData.set(ingredientMaster.ingredientNutritions);
          this.getFilteredCountryDetails();
        } else {
          this.gridRowData.set([]);
        }
        if (this._data.isEditable && this._data.hasAddPrivileges) {
          this.addNewRow();
        }
        this.initTable();
      });
    } else {
      this._libraryService
        .getIngredientNutritionalInfo(this._data?.ingredientId)
        .then((response) => {
          if (response?.ingredientNutritions) {
            this.gridRowData.set(response.ingredientNutritions);
          } else {
            this.gridRowData.set([]);
          }
          this.initTable();
        });
    }
  }

  /**
   * Initializes the table with column definitions.
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private initTable() {
    const titles = {
      save: this._translate.instant('save'),
      cancel: this._translate.instant('cancel'),
      edit: this._translate.instant('edit'),
    };
    let columnDefs = this.columnDefs().map((col: ColDef) => {
      col.headerName = this._translate.instant(col.field ?? '');
      col.headerClass = 'wrap-header';
      col.resizable = true;
      col.filter = col.field == 'action' ? '' : 'agTextColumnFilter';

      const unEditableColumns: string[] = [
        'regionName',
        'countryName',
        'isEnabled',
        'action',
      ];

      if (!unEditableColumns.includes(col.field as string)) {
        col.editable = (params: { data: { isEditable: boolean } }) => {
          return (
            params.data.isEditable &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          );
        };
      }

      if (col.field === 'regionName') {
        col.editable = (params: { data: { isEditable: boolean } }) => {
          return (
            params.data.isEditable &&
            this.activeNewRow &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          );
        };
        col.cellStyle = (params) => {
          return { pointerEvents: params.data.isEditable ? 'auto' : 'none' };
        };
        col.cellEditorSelector = (params: {
          data: { isEditable: boolean; isNewRow: boolean };
        }) => {
          if (
            params.data.isEditable &&
            params.data.isNewRow &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          ) {
            return {
              component: 'agRichSelectCellEditor',
              params: {
                values: this.regions,
              },
            };
          } else {
            return undefined;
          }
        };
        col.valueFormatter = (params) => {
          if (!params.data.isEditable) {
            return params.value ? (params.value as string) : '';
          }
          return params.value as string; // Ensure all code paths return a string
        };
        col.onCellValueChanged = (paramsChange) => {
          paramsChange.data.countryName = '';
          paramsChange.api.refreshCells({
            force: true, // Forces the renderer to be re-run even if data hasn't "changed"
            columns: ['countryName'],
          });
        };
      }

      if (this.selectedIndex === 1) {
        col.tooltipComponent = 'agTooltipComponent';
        col.tooltipField = col.field;
        col.tooltipValueGetter = (params: ITooltipParams) => {
          return params.value ? (params.value as string) : '';
        };
      }

      if (col.field === 'countryName') {
        col.tooltipComponent = 'agTooltipComponent';
        col.tooltipField = 'countryName';
        col.tooltipValueGetter = (params: ITooltipParams) => {
          return params.data?.countryName
            ? (params.data.countryName as string)
            : '';
        };
        col.editable = (params) => {
          return (
            (params.data.isEditable as boolean) &&
            this.activeNewRow &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          );
        };
        col.cellEditorSelector = (params) => {
          if (
            params.data.isEditable &&
            params.data.isNewRow &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          ) {
            return {
              component: 'agRichSelectCellEditor',
              params: {
                values: this.countryMaster
                  .filter(
                    (country: CountryResponseType) =>
                      country.regionName === params.data.regionName,
                  )
                  .map((country: CountryResponseType) => country.name),
              },
            };
          } else {
            return undefined;
          }
        };
        col.valueFormatter = (params) => {
          if (!params.data.isEditable) {
            return params.value ? (params.value as string) : '';
          }
          return params.value as string; // Ensure all code paths return a string
        };
      }
      if (col.field === 'modifiedOn') {
        col.filterParams = gridDateFormatter;
        col.filter = 'agDateColumnFilter';
        col.headerName = this._translate.instant('modifiedDate');
      }
      if (col.field === 'isEnabled') {
        col.headerName = this._translate.instant('soluSpecific');
        col.editable = (params) => {
          return (
            (params.data.isEditable as boolean) &&
            (this._data.hasUpdatePrivileges as boolean) &&
            this._data.ingredientTypeIdentifier !== 'INT-5'
          );
        };
        col.cellEditor = 'agCheckboxCellEditor';
      }
      col.onCellValueChanged = (paramsChange) => {
        paramsChange.data.cellValueChangeCount =
          (paramsChange.data.cellValueChangeCount || 0) + 1;
      };

      if (col.field === 'oldValue' || col.field === 'newValue') {
        col.cellRenderer = (params: ICellRendererParams) => {
          return convertToDecimel(params.value as string);
        };
      }

      if (col.field === 'action') {
        col.editable = false; // Disable editing for action column
        col.cellRenderer = (params: CellClickedEvent) => {
          return this.tableActionCellRenderer(params, titles);
        };
        col.onCellClicked = (params: CellClickedEvent) => {
          if (
            params.event &&
            params.event.target &&
            (params.event.target as HTMLElement).id === 'edit' &&
            !this.isActiveEdit()
          ) {
            this.tableEditClick(params);
          } else if (
            params.event &&
            params.event.target &&
            (params.event.target as HTMLElement).id === 'save'
          ) {
            this.tableSaveClick(params);
          } else if (
            params.event &&
            params.event.target &&
            (params.event.target as HTMLElement).id === 'cancel'
          ) {
            this.tableCancelClick(params);
          }
        };
        col.sortable = false; // Disable sorting for action column
        col.hide = !this._data.hasUpdatePrivileges;
      } else {
        col.onCellClicked = (params: CellClickedEvent) => {
          if (
            params.colDef.field !== 'action' &&
            params.data &&
            !params.data.isEditable
          ) {
            params.api.stopEditing();
          }
        };
      }

      return col;
    });
    if (this._data.ingredientTypeIdentifier === 'INT-5') {
      columnDefs = columnDefs.filter((col) => col.field !== 'action');
      this.columnDefs.set(columnDefs);
    } else {
      this.columnDefs.set(columnDefs);
    }
  }

  /**
   * Renders the action cell in the table based on the editing state.
   *
   * @private
   * @param {*} params - The parameters containing row and column information.
   * @returns {string} - The HTML string for the action cell.
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private tableActionCellRenderer(
    params: {
      data: { isEditable: boolean };
    },
    titles: { save: string; cancel: string; edit: string },
  ): string {
    if (params.data.isEditable) {
      return `<span title="${titles?.save}" class="material-symbols-outlined table-actions table-actions-save pop-up-action" id="save">check</span> 
                      &nbsp;
                      <span title="${titles?.cancel}" class="material-symbols-outlined table-actions table-actions-close pop-up-action" id="cancel">close</span>`;
    } else {
      return `<span title="${titles?.edit}" class="material-symbols-outlined table-actions" id="edit">edit</span>`;
    }
  }

  /**
   * Handles the click event for saving the edited row in the table.
   *
   * @private
   * @param {*} params - The parameters containing row and column information.
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private tableSaveClick(params: CellClickedEvent) {
    this.activeNewRow = false;
    params.api.stopEditing();
    this.updateNutritionalInfo(params);
    this.getFilteredCountryDetails();
    this.isActiveEdit.update(() => false);
  }

  /**
   * Handles the click event for canceling the edit action in the table.
   *
   * @private
   * @param {*} params - The parameters containing row and column information.
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private tableCancelClick(params: CellClickedEvent) {
    if (!params.data.nutritionId) {
      params.api.applyTransaction({
        remove: [params.data],
      });
      this.gridRowData.update((prev) => [
        ...prev.filter((item) => item.nutritionId !== params.data.nutritionId),
      ]);
    }
    gridCellDefaultView(
      params,
      ['regionName', 'countryName', 'isEnabled', 'action'],
      1,
    );
    this.activeNewRow = false;
    this.isActiveEdit.update(() => false);
  }

  /**
   * Handles the click event for editing a row in the table.
   *
   * @private
   * @param {*} params - The parameters containing row and column information.
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private tableEditClick(params: CellClickedEvent) {
    this.isActiveEdit.update(() => true);
    gridCellValueEdit(params, 'calories');
    gridRefreshCells(params, ['calories', 'isEnabled', 'action']);
  }

  /**
   * Adds a new row to the grid with default values.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  public addNewRow() {
    const newRow: IngredientNutritionalInfo = {
      regionName: '',
      countryName: '',
      calories: 0,
      fat: 0,
      totalSugars: 0,
      addedSugars: 0,
      dietaryFibers: 0,
      protein: 0,
      sodium: 0,
      totalCarbohydrates: 0,
      isEnabled: true,
      isNewRow: true,
      action: 'action',
      isEditable: true,
      activeField: 'regionName',
    };
    this.gridRowData.update((data) => [newRow, ...data]);
    this.activeNewRow = true;
    this.isActiveEdit.update(() => true);
  }

  /**
   * Updates the nutritional information of an ingredient.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  public updateNutritionalInfo(params: CellClickedEvent) {
    if (this._data.ingredientTypeIdentifier !== 'INT-5') {
      const updatedData: { ingredientNutritions: IngredientNutritionalInfo } = {
        ingredientNutritions: {
          ingredientId: Number(this._data.ingredientId),
          nutritionId: params.data?.nutritionId
            ? params.data.nutritionId
            : undefined,
          regionId: this.regionMaster.find(
            (region: RecipeRegion) => region.name === params.data.regionName,
          )?.id,
          regionName: params.data.regionName,
          countryId: params.data.countryId
            ? params.data.countryId
            : this.countryMaster.find(
                (country: CountryResponseType) =>
                  country.name === params.data.countryName,
              )?.id,
          countryName: params.data.countryName,
          calories: params.data.calories,
          fat: params.data.fat,
          totalSugars: params.data.totalSugars,
          addedSugars: params.data.addedSugars,
          dietaryFibers: params.data.dietaryFibers,
          protein: params.data.protein,
          sodium: params.data.sodium,
          totalCarbohydrates: params.data.totalCarbohydrates,
          isEnabled: params.data.isEnabled,
        },
      };
      if (
        !updatedData.ingredientNutritions.regionName ||
        !updatedData.ingredientNutritions.countryName
      ) {
        this._snackbarService.open({
          message: this._translate.instant('enterCountryFields'),
          type: 'error',
        });
        this.isActiveEdit.update(() => false);
        this.activeNewRow = true;
        params.data.isEditable = true;
        gridCellValueEdit(params, 'countryName');
        return;
      }
      const successMessage = params.data.isNewRow
        ? `${this._translate.instant('ingredientNutritionalInfoAddSuccess')} 
              ${this._data.ingredientName} ${this._translate.instant('ingredientNutritionalInfoAddSuccess1')} 
              ${params.data.regionName} ${this._translate.instant('ingredientNutritionalInfoAddSuccess2')} 
              ${params.data.countryName}`
        : `${this._translate.instant('ingredientNutritionalInfoUpdateSuccess')} 
              ${this._data.ingredientIdentifier}`;

      const dialogRef = this._dialog.open(UpdateAspAlertDialogComponent, {
        data: {
          nutritionalInfoData: true,
          countryName: params.data.countryName,
        },
        minWidth: '32rem',
        maxWidth: '35rem',
        height: '18rem',
        disableClose: true,
        panelClass: 'update-asp-dialog',
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'confirmed') {
          this._libraryService
            .updateIngredientNutritionalInfo(updatedData)
            .then((response) => {
              if (response?.isSuccess) {
                this.activeNewRow = false;
                params.data.isNewRow = false;
                params.data.nutritionId =
                  response.ingredientNutritions.nutritionId;
                params.data.countryId = response.ingredientNutritions.countryId;
                params.api.applyTransaction({
                  update: [params.data],
                });
                params.data.cellValueChangeCount = 0;
                gridCellDefaultView(params, [
                  'regionName',
                  'countryName',
                  'isEnabled',
                  'action',
                ]);
                this._snackbarService.open({
                  message: successMessage,
                  type: 'success',
                });
              }
            });
        } else if (result === 'cancelled') {
          if (!params.data.nutritionId) {
            params.api.applyTransaction({
              remove: [params.data],
            });
            this.gridRowData.update((prev) => [
              ...prev.filter(
                (item) => item.nutritionId !== params.data.nutritionId,
              ),
            ]);
          }
          gridCellDefaultView(
            params,
            ['regionName', 'countryName', 'isEnabled', 'action'],
            1,
          );
          this.activeNewRow = false;
          this.isActiveEdit.update(() => false);
        }
      });
    }
  }

  /**
   * Toggles the enabled state of a nutritional information entry.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  public toggleNutritionalInfo(params: SelectionChangedEvent) {
    if (this._data.ingredientTypeIdentifier !== 'INT-5') {
      const updatedData: { ingredientNutritions: IngredientNutritionalInfo } = {
        ingredientNutritions: {
          ...params.api.getSelectedNodes()[0]?.data,
          isEnabled: params.api.getSelectedNodes()[0]?.data.isEnabled,
        },
      };
      this._libraryService
        .updateIngredientNutritionalInfo(updatedData)
        .then((response) => {
          if (response?.isSuccess) {
            this._snackbarService.open({
              message: `${this._translate.instant('ingredientNutritionalInfoUpdateSuccess')} 
              ${this._data.ingredientIdentifier}`,
              type: 'success',
            });
          }
        });
    }
  }

  /**
   * Filters the available countries based on the ingredient's nutritional information.
   *
   * @private
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private getFilteredCountryDetails() {
    this.availableNutriCountries = this.gridRowData()
      .map((nutriInfo: IngredientNutritionalInfo) => nutriInfo.countryName)
      .filter((name): name is string => name !== undefined);
    this.countryMaster = this.countryMaster.filter(
      (country: CountryResponseType) =>
        country.name !== undefined &&
        !this.availableNutriCountries
          .filter((name): name is string => name !== undefined)
          .includes(country.name),
    );
  }

  /**
   * Handles tab change event.
   *
   * @param {number} index
   * @memberof UpdateNutritionalInformationDialogComponent
   */

  public onTabChange(index: number) {
    this.selectedIndex = index;
    this.gridRowData.update(() => []);
    this.columnDefs.update(() => []);
    this.gridOptions.update(() => ({}));
    this.rowModelSwitch$.next(true);
    this.isSearch = true;
  }

  /**
   * Creates server-side grid options.
   *
   * @private
   * @return {*}  {GridOptions}
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private createServerSideGridOptions(): GridOptions {
    let gridOptions: GridOptions = {};
    if (this.selectedIndex === 1) {
      gridOptions = {
        rowModelType: 'serverSide',
        cacheBlockSize: 30,
        gridId: 'serverGrid',
        onGridReady: (params) => {
          params.api.setGridOption(
            'serverSideDatasource',
            this._libraryService.createServerSidePagination(
              CONSTANTS_DETAILS.INGREDIENT_NUTRITIONAL_INFO_HISTORY_URL,
              parseInt(this._data.ingredientId),
              this._datePipe,
            ),
          );
        },
        getRowId: (params: GetRowIdParams<RowData>) =>
          params.data.id.toString() as string,
        onPaginationChanged: (param: PaginationChangedEvent) => {
          if (param.newPageSize) {
            param.api.refreshServerSide({ purge: true });
          }
        },
        getMainMenuItems: (params) => {
          return params.defaultItems.filter((item) => item !== 'columnFilter');
        },
      };
    } else {
      gridOptions = {
        onSelectionChanged: (event: SelectionChangedEvent) => {
          this.toggleNutritionalInfo(event);
        },
        headerHeight: 60,
        enableBrowserTooltips: true,
        tooltipShowDelay: 0,
        tooltipHideDelay: 2000,
        gridId: 'singleGrid',
        onCellEditingStarted: (params) => {
          const fieldsUndo = ['regionName', 'countryName', 'action'];
          if (fieldsUndo.indexOf(params.colDef.field || '') === -1) {
            params.data.cellValueChangeCount =
              (params.data.cellValueChangeCount || 0) + 1;
          }
        },
      };
    }
    return { ...TABLE_CONSTANTS.AG_GRID_CONFIG, ...gridOptions };
  }

  /**
   *  Listens for grid switch events and updates the grid accordingly.
   *
   * @private
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private gridSwitchListener() {
    this.rowModelSwitch$.pipe(debounceTime(0)).subscribe((isChange) => {
      if (isChange) {
        this.tabSwitchListener();
      }
    });
  }

  /**
   * Listens for tab switch events and updates the active tab.
   *
   * @private
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  private tabSwitchListener() {
    if (this.selectedIndex === 0) {
      this.gridOptions.set(this.createServerSideGridOptions());
      this.columnDefs.set(TABLE_CONSTANTS.INGREDIENT_NUTRITIONAL_INFO_TABLE);
      this.getIngredientNutritionalInfo();
    } else {
      this.columnDefs.set(
        TABLE_CONSTANTS.INGREDIENT_NUTRITIONAL_INFO_HISTORY_TABLE,
      );
      this.gridOptions.set(this.createServerSideGridOptions());
      this.initTable();
      this._data.isEditable = false;
      this.isActiveEdit.update(() => false);
      this.gridRowData.update(() => [
        { isServerSide: false } as IngredientNutritionalInfo,
      ]);
    }
  }

  /**
   *  Cleans up subscriptions and resources when the component is destroyed.
   *
   * @memberof UpdateNutritionalInformationDialogComponent
   */
  ngOnDestroy(): void {
    this.gridRowData.set([]);
    this.columnDefs.set([]);
    this.rowModelSwitch$.complete();
    this.rowModelSwitch$.unsubscribe();
  }
}
